import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  ButtonIcon,
  Flex,
  Select,
  Text,
  TextAreaField,
} from '@backstage/ui';
import {
  RiAddLine,
  RiEyeLine,
  RiEyeOffLine,
  RiDeleteBinLine,
} from '@remixicon/react';
import type { CheckDefinition } from '@internal/backstage-plugin-pulse-common';
import { selectedKey } from '../../../select-helpers';
import { CheckYamlPreview } from './check-yaml-preview';
import { FieldError } from './field-error';

type CheckRow = {
  key: string;
  checkId: string;
  /** Added since the last validation run, so not reported or flagged yet. */
  isNew?: boolean;
};

export type TrackCheckPickerProps = {
  label: string;
  description: string;
  addButtonLabel: string;
  checks: CheckDefinition[];
  value: string[];
  onChange: (checkIds: string[]) => void;
  levelDescription: string;
  onLevelDescriptionChange: (description: string) => void;
  onCheckExpanded?: (checkId: string) => void;
  expandedCheckId?: string;
  /** Keeps one row available for required check groups. */
  isRequired?: boolean;
  /** Increments each time the form is validated. */
  validationAttempt?: number;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isInvalid?: boolean;
};

/** One dropdown per check, with add, inline preview, and remove controls. */
export const TrackCheckPicker = ({
  label,
  description,
  addButtonLabel,
  checks,
  value,
  onChange,
  levelDescription,
  onLevelDescriptionChange,
  onCheckExpanded,
  expandedCheckId,
  isRequired = false,
  validationAttempt = 0,
  isLoading = false,
  isReadOnly = false,
  isInvalid = false,
}: TrackCheckPickerProps) => {
  // Rows are local so a row can exist before a check has been chosen for it.
  const nextKey = useRef(0);
  const createKey = () => {
    nextKey.current += 1;
    return `row-${nextKey.current}`;
  };

  const [rows, setRows] = useState<CheckRow[]>(() => {
    if (value.length > 0) {
      return value.map(checkId => ({ key: createKey(), checkId }));
    }
    return isRequired ? [{ key: createKey(), checkId: '' }] : [];
  });

  const labelByCheckId = useMemo(
    () => new Map(checks.map(check => [check.id, check.name])),
    [checks],
  );

  const checkOptions = useMemo(
    () =>
      checks
        .map(check => ({ value: check.id, label: check.name }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [checks],
  );

  const commit = (updated: CheckRow[]) => {
    setRows(updated);
    // Empty rows are reported so the form can flag them, but a row added since
    // the last validation run stays out of scope until the next one.
    onChange(updated.map(row => row.checkId));
  };

  const lastValidationAttempt = useRef(validationAttempt);

  useEffect(() => {
    if (lastValidationAttempt.current === validationAttempt) {
      return;
    }

    lastValidationAttempt.current = validationAttempt;
    setRows(currentRows => {
      const updatedRows = currentRows.map(row => ({
        ...row,
        isNew: false,
      }));

      onChange(updatedRows.map(row => row.checkId));
      return updatedRows;
    });
  }, [onChange, validationAttempt]);

  const optionsForRow = (row: CheckRow) => {
    const takenElsewhere = new Set(
      rows.filter(other => other.key !== row.key).map(other => other.checkId),
    );
    return checkOptions.filter(option => !takenElsewhere.has(option.value));
  };

  return (
    <Flex direction="column" gap="2">
      <Flex direction="column" gap="1">
        <Text variant="body-medium">{label}</Text>
        <Text variant="body-small" color="secondary">
          {description}
        </Text>
        <TextAreaField
          label={`Explain what the ${label.toLowerCase()} measure and why it matters. (optional)`}
          value={levelDescription}
          onChange={onLevelDescriptionChange}
          rows={2}
          isDisabled={isReadOnly}
        />
      </Flex>

      {rows.map(row => {
        const checkName = row.checkId
          ? labelByCheckId.get(row.checkId) ?? row.checkId
          : undefined;
        const isEmptyRowInvalid = isInvalid && !row.checkId && !row.isNew;
        const check = checks.find(current => current.id === row.checkId);
        const isExpanded =
          Boolean(row.checkId) && row.checkId === expandedCheckId;
        const canRemoveRow = (!isRequired || rows.length > 1) && !isReadOnly;

        return (
          <Flex key={row.key} direction="column" gap="1">
            <Flex
              align="center"
              gap="2"
              p="1"
              style={{
                background: isExpanded ? 'var(--bui-bg-surface-2)' : undefined,
                borderRadius: 'var(--bui-radius-2)',
              }}
            >
              <Select
                aria-label={label}
                options={optionsForRow(row)}
                value={row.checkId}
                isInvalid={isEmptyRowInvalid}
                isDisabled={isLoading || isReadOnly}
                search={{ placeholder: 'Search checks' }}
                onChange={keys => {
                  const checkId = selectedKey(keys) ?? '';
                  commit(
                    rows.map(current =>
                      current.key === row.key
                        ? { ...current, checkId, isNew: false }
                        : current,
                    ),
                  );
                  if (row.checkId === expandedCheckId) {
                    onCheckExpanded?.(row.checkId);
                  }
                }}
              />
              {row.checkId && (
                <Button
                  variant="tertiary"
                  size="small"
                  iconStart={isExpanded ? <RiEyeOffLine /> : <RiEyeLine />}
                  onPress={() => onCheckExpanded?.(row.checkId)}
                >
                  {isExpanded ? 'Hide preview' : 'Preview'}
                </Button>
              )}
              {canRemoveRow ? (
                <ButtonIcon
                  variant="tertiary"
                  size="small"
                  icon={<RiDeleteBinLine />}
                  aria-label={
                    checkName ? `Remove ${checkName}` : 'Remove check'
                  }
                  onPress={() =>
                    commit(rows.filter(current => current.key !== row.key))
                  }
                />
              ) : null}
            </Flex>
            {isExpanded && check ? <CheckYamlPreview check={check} /> : null}
            {isEmptyRowInvalid && (
              <FieldError message="Select a check, or delete this row" />
            )}
          </Flex>
        );
      })}

      <Flex justify="start">
        <Button
          variant="secondary"
          size="small"
          iconStart={<RiAddLine />}
          isDisabled={
            isLoading || isReadOnly || rows.length >= checkOptions.length
          }
          onPress={() =>
            commit([...rows, { key: createKey(), checkId: '', isNew: true }])
          }
        >
          {addButtonLabel}
        </Button>
      </Flex>
    </Flex>
  );
};
